import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm, type Category } from '../api/types'
import { toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Spinner, TextInput,
} from '@shop/ui'

/**
 * الأقسام — <b>«القسم» في الواجهة و<c>Category</c> في الشيفرة</b>.
 *
 * <p>
 * ⚠ <b>ولا زرَّ حذفٍ هنا، ولا مسارَ حذفٍ في الخادم أصلًا.</b> القسمُ إلزاميٌّ
 * على كلّ صنف، فحذفُ قسمٍ يترك أصنافَه تشير إلى ما لا وجودَ له — وتقريرَ
 * السنة الماضية بلا عمود. <b>والتعطيلُ هو الأرشفة</b>: يُخرج القسمَ من قوائم
 * الاختيار ومن شبكة الموقع ويُبقي ما مضى.
 * </p>
 *
 * <p>
 * ⚠ <b>وحتى التعطيلُ يُمنع ما دام في القسم صنفٌ فعّال</b> (<c>catalog.category_in_use</c>)
 * — والعددُ معروضٌ بجانب كلّ قسمٍ قبل أن يُحاوَل، فلا يُكتشف المنعُ بالضغط.
 * </p>
 *
 * <p>
 * ⚠ <b>وكلُّ تغييرٍ يُرى على الموقع يُطلق إعادةَ بناء</b> — الاسمُ والترتيبُ
 * والتفعيل. والاسمُ الإنجليزيُّ لا يُعرض فلا يُطلق. وتعديلاتٌ متقاربةٌ تُجمَع
 * في بناءٍ واحد.
 * </p>
 */
export default function Categories() {
  const { can } = useAuth()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)

  const categories = useQuery({
    queryKey: ['categories', 'all'],
    queryFn: () => api<Category[]>('/api/v1/catalog/categories?includeInactive=true'),
  })

  const mayEdit = can(Perm.productEdit)

  return (
    <div className="space-y-4">
      <Panel
        title="الأقسام"
        actions={
          mayEdit && (
            <Button tone="primary" onClick={() => setAdding(!adding)}>
              {adding ? 'إلغاء' : 'قسمٌ جديد'}
            </Button>
          )
        }
      >
        <p className="mb-4 text-xs text-ink-soft">
          القسمُ إلزاميٌّ على كلّ صنف، وهو نفسُه الذي يظهر على الموقع العامّ
          وفي التقارير — <b>ولا قائمةَ ثانيةَ للموقع</b>.
        </p>

        {adding && (
          <div className="mb-4">
            <CategoryForm onDone={() => setAdding(false)} />
          </div>
        )}

        {categories.isPending && <Spinner />}
        {categories.isError && <ErrorBanner error={categories.error} />}
        {categories.data?.length === 0 && <Empty>لا أقسامَ بعد.</Empty>}

        <ul className="space-y-2">
          {(categories.data ?? []).map((category) => (
            <li key={category.id}>
              <div
                className={
                  'rounded-md border px-3 py-2 '
                  + (category.isActive ? 'border-line' : 'border-line bg-surface opacity-70')
                }
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium">
                    {category.nameAr}
                    {!category.isActive && <span className="mr-2 text-xs text-warn">معطَّل</span>}
                  </span>

                  <span className="tabular text-xs text-ink-soft">
                    ترتيبُ الظهور: {category.sortOrder}
                  </span>
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-soft">
                  {category.nameEn && <span dir="ltr">{category.nameEn}</span>}

                  {/*
                    ⚠ **والعددُ رابطٌ لا رقمٌ يُقرأ**: «١٢ صنفًا» ثمّ البحثُ
                      عنها يدويًّا في شاشة الأصناف عملٌ يُعاد كلَّ مرّة.
                  */}
                  {category.productCount > 0 ? (
                    <Link
                      className="text-brand hover:underline"
                      to={`/products?categoryId=${category.id}`}
                    >
                      {category.productCount} صنفًا فعّالًا
                    </Link>
                  ) : (
                    <span>لا أصنافَ فيه</span>
                  )}
                </div>

                {mayEdit && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button onClick={() => setEditing(editing === category.id ? null : category.id)}>
                      {editing === category.id ? 'إغلاق' : 'تعديل'}
                    </Button>

                    <ActiveControl category={category} />
                  </div>
                )}
              </div>

              {editing === category.id && (
                <div className="mt-2">
                  <CategoryForm category={category} onDone={() => setEditing(null)} />
                </div>
              )}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  )
}

/**
 * التفعيلُ والتعطيل.
 *
 * ⚠ <b>والزرُّ يُعطَّل قبل الضغط حين يكون القسمُ مستعمَلًا</b> — والخادمُ يمنع
 *   على أيّ حال (<c>catalog.category_in_use</c>). والاثنان معًا مقصودان:
 *   الشاشةُ تشرح، والخادمُ يضمن — ولا يُستغنى بالأولى عن الثاني.
 */
function ActiveControl({ category }: { category: Category }) {
  const queryClient = useQueryClient()

  const toggle = useMutation({
    mutationFn: (isActive: boolean) =>
      api<Category>(`/api/v1/catalog/categories/${category.id}`, {
        method: 'PATCH',
        body: {
          nameAr: category.nameAr,
          nameEn: category.nameEn,
          sortOrder: category.sortOrder,
          isActive,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
  })

  if (!category.isActive) {
    return (
      <Button disabled={toggle.isPending} onClick={() => toggle.mutate(true)}>
        {toggle.isPending ? 'يُفعَّل…' : 'تفعيل'}
      </Button>
    )
  }

  const blocked = category.productCount > 0

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        disabled={blocked || toggle.isPending}
        onClick={() => toggle.mutate(false)}
      >
        {toggle.isPending ? 'يُعطَّل…' : 'تعطيل'}
      </Button>

      {blocked && (
        <span className="text-xs text-ink-soft">
          انقل أصنافَه إلى قسمٍ آخرَ أوّلًا.
        </span>
      )}

      {toggle.isError && (
        <div className="w-full">
          <ErrorBanner error={toggle.error} />
        </div>
      )}
    </div>
  )
}

/** إنشاءُ قسمٍ أو تعديلُ اسمه وترتيبه. */
function CategoryForm({ category, onDone }: { category?: Category; onDone: () => void }) {
  const queryClient = useQueryClient()
  const [nameAr, setNameAr] = useState(category?.nameAr ?? '')
  const [nameEn, setNameEn] = useState(category?.nameEn ?? '')
  const [sortOrder, setSortOrder] = useState(String(category?.sortOrder ?? 0))

  const save = useMutation({
    mutationFn: () =>
      api<Category>(
        category ? `/api/v1/catalog/categories/${category.id}` : '/api/v1/catalog/categories',
        {
          method: category ? 'PATCH' : 'POST',
          body: {
            nameAr: nameAr.trim(),
            nameEn: nameEn.trim() === '' ? null : nameEn.trim(),
            sortOrder: toNumber(sortOrder) ?? 0,
            isActive: category?.isActive ?? true,
          },
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['categories'] })
      onDone()
    },
  })

  const blank = nameAr.trim().length === 0

  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="اسمُ القسم" hint="كما يراه الزبونُ على الموقع">
          <TextInput value={nameAr} onChange={setNameAr} placeholder="كفرات" />
        </Field>

        <Field label="بالإنجليزيّة" hint="لا يظهر على الموقع">
          <TextInput value={nameEn} onChange={setNameEn} dir="ltr" placeholder="Cases" />
        </Field>

        <Field label="ترتيبُ الظهور" hint="الأصغرُ أوّلًا — في القوائم وعلى الموقع">
          <NumberInput value={sortOrder} onChange={setSortOrder} placeholder="0" />
        </Field>
      </div>

      {category && (
        <div className="mt-3">
          <Banner tone="info">
            تغييرُ الاسم أو الترتيب يُعيد بناءَ الموقع العامّ خلال دقائق.
          </Banner>
        </div>
      )}

      {save.isError && (
        <div className="mt-3">
          <ErrorBanner error={save.error} />
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button tone="primary" disabled={blank || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'يُحفظ…' : category ? 'حفظُ التعديل' : 'إنشاء'}
        </Button>

        <Button onClick={onDone}>إلغاء</Button>
      </div>
    </div>
  )
}
